/*
 * The client-side view of App\Services\Api\Auth\AbilityCatalog: what each
 * ability domain is, how it is presented, and the small algebra the
 * GrantsEditor needs to turn a level-per-domain picker with one ontology
 * selection at its root into a flat ability list with per-ability ontology
 * scopes. Shared by the API-key manager and the role editor, so nothing in
 * here may assume it is talking about a key.
 */

// Mirrors AbilityCatalog::ONTOLOGY_SCOPABLE — abilities that may be
// restricted to specific ontologies on a grant.
export const ONTOLOGY_SCOPABLE = [
  'ontologies:read',
  'ontologies:write',
  'passports:read',
  'passports:write',
  'compositions:read',
  'compositions:write',
  'imports:read',
  'imports:write',
];

// Domains whose abilities accept a per-ontology restriction.
export const SCOPABLE_DOMAINS = [...new Set(ONTOLOGY_SCOPABLE.map(a => a.split(':')[0]))];

// Mirrors AbilityCatalog::REQUIRES — what holding an ability presupposes.
// `ontologies:read` is the root of every ontology-bound grant: the abilities
// that depend on it can never reach an ontology it does not, and releasing
// presupposes writing passports.
export const REQUIRES = {
  'ontologies:write': 'ontologies:read',
  'passports:read': 'ontologies:read',
  'passports:write': 'ontologies:read',
  'compositions:read': 'ontologies:read',
  'compositions:write': 'ontologies:read',
  'imports:read': 'ontologies:read',
  'imports:write': 'ontologies:read',
  'releases:write': 'passports:write',
};

// The domain whose ontology list every other scopable domain inherits, and
// the domains that inherit it. `ontologies:write` shares the root's own list
// rather than narrowing it, so it is not listed as a dependent.
export const ROOT_DOMAIN = 'ontologies';
export const DEPENDENT_DOMAINS = ['passports', 'compositions', 'imports'];

// Presentation for each ability domain: a human title, a one-line description
// of what the domain actually reaches, and the section it groups under.
//
// Every domain in AbilityCatalog::ALL has an entry here, deliberately. This
// map used to carry a fallback that swept anything it did not recognise into
// an "Other" bucket, which meant every ability added to the backend since it
// was written — events, webhooks, design, suppliers, data requests, team,
// keys — landed in a junk drawer with no title and no description. There is
// no fallback now: an undescribed domain is surfaced as an explicit gap (see
// abilityDomains) rather than quietly filed away.
//
// Key order is display order within a section, so a domain sits next to what
// it is about (Releases beside Passports) rather than wherever the backend
// happened to append it to the catalog.
export const DOMAIN_META = {
  templates: {
    title: 'Templates',
    description: 'Reusable ontology blueprints.',
    section: 'Schema',
  },
  ontologies: {
    title: 'Ontologies',
    description: 'Live schemas built from templates.',
    section: 'Schema',
  },
  compositions: {
    title: 'Compositions',
    description: 'Presentation layouts for an ontology.',
    section: 'Schema',
  },
  passports: {
    title: 'Passports',
    description: 'Individual records within an ontology.',
    section: 'Data',
  },
  releases: {
    title: 'Releases',
    description: "Releasing passports, and revising or archiving ones that have been released. A passport's first release is the billing event; re-releases are free.",
    section: 'Data',
  },
  imports: {
    title: 'Imports',
    description: 'Bulk spreadsheet imports and their row-level results.',
    section: 'Data',
  },
  files: {
    title: 'Files',
    description: 'Uploaded files, used by imports and file properties.',
    section: 'Data',
  },
  suppliers: {
    title: 'Suppliers',
    description: 'Companies outside your team, and the values you ask them for.',
    section: 'Collaboration',
  },
  data_requests: {
    title: 'Data requests',
    description: 'Values you ask suppliers for, and the answers you send back to customers.',
    section: 'Collaboration',
  },
  external_access: {
    title: 'External access',
    description: 'Service partners, edit rules, access rules, requests and changes made from outside the team. Approving or correcting a change also needs Passports write on its ontology.',
    section: 'Collaboration',
  },
  partner_links: {
    title: 'Partner links',
    description: 'The brands this team works for as a service partner. Write accepts links, presents the company seal and applies to the registry.',
    section: 'Collaboration',
  },
  partner_work: {
    title: 'Partner work',
    description: 'Update passports of linked brands, under each brand’s edit rules.',
    section: 'Collaboration',
  },
  events: {
    title: 'Events',
    description: 'See what changed across your team over the last 30 days.',
    section: 'Automation',
  },
  webhook_endpoints: {
    title: 'Webhook endpoints',
    description: 'Send events to your own systems as they happen.',
    section: 'Automation',
  },
  lab: {
    title: 'Synex Lab',
    description: 'PassQL queries, transforms and rollbacks.',
    section: 'Insights',
  },
  consul: {
    title: 'Consul',
    description: 'Use Consul, the in-app agent. It acts with the member’s own permissions and asks before every change.',
    section: 'Workspace',
  },
  design: {
    title: 'Design',
    description: 'The white-label theme on your public passport pages.',
    section: 'Workspace',
  },
  team: {
    title: 'Team settings',
    description: 'The team’s name and identity. Reading also lists the member roster — names and emails.',
    section: 'Workspace',
  },
  members: {
    title: 'Members',
    description: 'Invite and remove members, and assign their roles.',
    section: 'Workspace',
  },
  roles: {
    title: 'Roles',
    description: 'Define and edit the roles members hold.',
    section: 'Workspace',
  },
  api_keys: {
    title: 'API keys',
    description: 'List, create, roll and revoke API keys.',
    section: 'Workspace',
  },
};

export const SECTION_ORDER = [
  'Schema',
  'Data',
  'Collaboration',
  'Automation',
  'Insights',
  'Workspace',
];

// Where a domain nobody has described yet is shown. It is grouped rather than
// hidden, and the row itself says out loud that this page needs updating —
// the point is that a new backend ability is impossible to miss, not that it
// has somewhere tidy to sit.
export const UNDESCRIBED_SECTION = 'Workspace';

// Abilities that hand out more than data. Each is shown with a warning
// treatment whether or not it is currently granted, so an operator reading
// the picker sees the weight of the choice before making it.
export const SENSITIVE_ABILITIES = {
  'api_keys:write': 'Mints, rolls and revokes API keys — a key with this can outlive its own revocation.',
  'webhook_endpoints:write': 'Creates standing configuration that ships event data to outside URLs.',
  'releases:write': "A passport's first release is a billing event — recorded on the ledger and charged to the team. Re-releases are free.",
};

export const LEVEL_LABEL = {
  none: 'No access',
  read: 'Read',
  write: 'Write',
  readwrite: 'Read and write',
  use: 'Use',
};

export function domainOf(ability) {
  return ability.split(':')[0];
}

export function humanizeDomain(domain) {
  const words = domain.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const META_RANK = new Map(Object.keys(DOMAIN_META).map((domain, index) => [domain, index]));

// Group the flat ability list into domains, in DOMAIN_META order with any
// undescribed domain after the described ones, and remember which actions
// (read/write) each domain exposes. Only namespaced `domain:action` abilities
// are real scopes; bare permissions (create, read, update, delete) leak in
// from the team-role definitions Jetstream unions into its permission list,
// and are dropped here.
export function abilityDomains(availablePermissions) {
  const actionsByDomain = {};
  // Undescribed domains keep the order they were first seen in; recorded
  // here so the comparator is two map lookups, not an indexOf per compare.
  const firstSeen = new Map();
  for (const ability of availablePermissions) {
    if (!ability.includes(':')) continue;
    const [domain, action] = ability.split(':');
    if (!actionsByDomain[domain]) {
      actionsByDomain[domain] = [];
      firstSeen.set(domain, firstSeen.size);
    }
    actionsByDomain[domain].push(action);
  }

  const rank = domain =>
    META_RANK.has(domain) ? META_RANK.get(domain) : META_RANK.size + firstSeen.get(domain);

  return [...firstSeen.keys()]
    .sort((a, b) => rank(a) - rank(b))
    .map(domain => {
      const meta = DOMAIN_META[domain];

      return {
        domain,
        actions: actionsByDomain[domain],
        scopable: SCOPABLE_DOMAINS.includes(domain),
        title: meta ? meta.title : humanizeDomain(domain),
        description: meta ? meta.description : '',
        section: meta ? meta.section : UNDESCRIBED_SECTION,
        undescribed: !meta,
      };
    });
}

// Every real API ability the backend offers, in catalog order.
export function scopedAbilities(availablePermissions) {
  return availablePermissions.filter(a => a.includes(':'));
}

// Access levels for a domain, narrowed to the actions it supports. Read and
// write are independent grants — a bare Write is offered so ingestion keys
// (e.g. a factory line issuing passports it can't read back) are expressible.
// Labels are one or two words because they sit side by side in a segmented
// control, where "Read and write" beside "Write" already says "write only".
export function domainLevels(domain, actions) {
  const hasRead = actions.includes('read');
  const hasWrite = actions.includes('write');
  const levels = [{ value: 'none', label: 'None', grants: [] }];
  if (hasRead) {
    levels.push({ value: 'read', label: 'Read', grants: [`${domain}:read`] });
  }
  if (hasWrite) {
    levels.push({ value: 'write', label: 'Write', grants: [`${domain}:write`] });
  }
  if (hasRead && hasWrite) {
    levels.push({
      value: 'readwrite',
      label: 'Read and write',
      grants: [`${domain}:read`, `${domain}:write`],
    });
  }
  // An ability that is neither half of a domain (consul:use) is a level of its own.
  if (actions.includes('use')) {
    levels.push({ value: 'use', label: 'Use', grants: [`${domain}:use`] });
  }
  return levels;
}

export function selectedLevel(domain, abilities) {
  const canRead = abilities.includes(`${domain}:read`);
  const canWrite = abilities.includes(`${domain}:write`);
  if (canRead && canWrite) return 'readwrite';
  if (canWrite) return 'write';
  if (canRead) return 'read';
  if (abilities.includes(`${domain}:use`)) return 'use';
  return 'none';
}

export function setDomainLevel(domain, levels, value, selected, setSelected) {
  const grants = levels.find(l => l.value === value)?.grants ?? [];
  const without = selected.filter(a => domainOf(a) !== domain);
  setSelected([...without, ...grants]);
}

/*
 * Presets: one control that sets every domain at once.
 *
 * A preset is applied, never stored. Which one is "active" is derived from
 * the current selection on every render, so touching a single domain
 * afterwards drops the control to Custom by itself — there is no second
 * source of truth that could keep claiming "Read only" over a selection that
 * is no longer read-only. Rebuilding a preset's exact set by hand honestly
 * reads as that preset again, because it is that preset.
 */
export const PRESETS = [
  { value: 'read', label: 'Read only' },
  { value: 'write', label: 'Write only' },
  { value: 'readwrite', label: 'Read and write' },
];

export function presetAbilities(availablePermissions, preset) {
  const all = scopedAbilities(availablePermissions);
  if (preset === 'read') return all.filter(a => a.endsWith(':read'));
  // Every write presupposes the root (REQUIRES), so "Write only" carries
  // `ontologies:read` — without it the server refuses the whole map as
  // grant_inconsistent, and a preset that cannot be saved is not a preset.
  if (preset === 'write') return all.filter(a => a.endsWith(':write') || a === 'ontologies:read');
  if (preset === 'readwrite') return all;
  return [];
}

export function activePreset(availablePermissions, selected) {
  if (selected.length === 0) return 'none';

  const chosen = new Set(selected);
  for (const preset of PRESETS) {
    const want = presetAbilities(availablePermissions, preset.value);
    if (want.length === chosen.size && want.every(a => chosen.has(a))) {
      return preset.value;
    }
  }

  return 'custom';
}

/*
 * The ceiling: an actor's own grants, which bound what they may hand out.
 * The server refuses anything wider (RoleService / MembershipService /
 * ApiKeyService all run Grants::covers), so the editor mirrors the rule up
 * front. Everything here is pure so a dialog can ask "would this save?"
 * without rendering the editor — that is what editorProblems is for.
 */

export function holds(ceiling, ability) {
  return Boolean(ceiling) && Object.prototype.hasOwnProperty.call(ceiling, ability);
}

// The one sentence every control disabled by the ceiling carries.
export const CEILING_TITLE = "You can't grant a permission you don't hold";

// Mirrors Grants::covers(): every ability in `wanted` is held, and where the
// holder's own grant is scoped the wanted scope names a subset of it. A
// team-wide ask under a scoped holding is the widest request, so it fails.
export function covers(holder, wanted) {
  for (const [ability, scope] of Object.entries(wanted || {})) {
    if (!holds(holder, ability)) return false;

    const mine = holder[ability];
    if (mine === null || mine === undefined) continue;

    if (!scope || !Array.isArray(scope.ontologies)) return false;
    if (scope.ontologies.some(id => !mine.ontologies.includes(id))) return false;
  }

  return true;
}

// Mirrors Grants::union() over any number of maps: an ability held team-wide
// by any of them is team-wide in the result, otherwise the ontology lists
// merge. A member's effective access is exactly this over their roles and
// their override.
export function unionGrants(...maps) {
  const result = {};
  for (const map of maps) {
    for (const [ability, scope] of Object.entries(map || {})) {
      const scoped = scope && Array.isArray(scope.ontologies);
      if (!holds(result, ability)) {
        result[ability] = scoped ? { ontologies: [...scope.ontologies] } : null;
      } else if (result[ability] !== null && scoped) {
        result[ability] = { ontologies: [...new Set([...result[ability].ontologies, ...scope.ontologies])] };
      } else if (!scoped) {
        result[ability] = null;
      }
    }
  }

  return result;
}

// The ontologies a scope may name for these grants under the ceiling: null
// when unconstrained (no ceiling, or every granted ability held team-wide),
// otherwise the intersection of the scoped lists. One list serves the whole
// domain, so what it names has to satisfy every ability it fans out to.
export function ceilingScope(ceiling, grants) {
  if (!ceiling) return null;

  let allowed = null;
  for (const slug of grants) {
    const scope = ceiling[slug];
    const ids = scope && Array.isArray(scope.ontologies) ? scope.ontologies : null;
    if (!ids) continue;
    allowed = allowed === null ? [...ids] : allowed.filter(id => ids.includes(id));
  }

  return allowed;
}

// Which of a domain's levels the ceiling can grant, and a one-word summary
// of the row: 'full' (everything), 'partial' (some), 'none' (nothing to
// choose). A level is out of reach when an ability it grants is not held —
// or when every ability is held but their scoped lists share no ontology,
// because then there is nothing the level could legally name.
export function ceilingFor(ceiling, levels) {
  if (!ceiling) return { disabledValues: new Set(), state: 'full' };

  const disabledValues = new Set();
  for (const level of levels) {
    if (level.value === 'none') continue;

    const held = level.grants.every(slug => holds(ceiling, slug));
    const reach = held ? ceilingScope(ceiling, level.grants) : null;

    if (!held || (reach !== null && reach.length === 0)) {
      disabledValues.add(level.value);
    }
  }

  const grantable = levels.filter(l => l.value !== 'none' && !disabledValues.has(l.value)).length;

  return {
    disabledValues,
    state: grantable === 0 ? 'none' : grantable === levels.length - 1 ? 'full' : 'partial',
  };
}

// Why the list a domain reaches (effectiveScope) would be refused under the
// ceiling's `allowed` list, or null. "Every ontology" (null) under a scoped
// ceiling is the widest possible request, not the narrowest, so it is the one
// case where "no restriction" stops being harmless.
export function scopeProblem(effective, allowed) {
  if (allowed === null) return null;

  if (allowed.length === 0) {
    return 'this level cannot be granted — your own permissions reach no ontology these abilities share.';
  }

  if (effective === null) {
    return 'every ontology is more than you hold — limit it to the ontologies you hold.';
  }

  const outside = effective.filter(id => !allowed.includes(id));
  return outside.length > 0 ? `not in your own permission: ${outside.join(', ')}.` : null;
}

const titleOf = domain => DOMAIN_META[domain]?.title ?? humanizeDomain(domain);

// The dependency sentence in the row's own words: what is missing is named
// the way the editor names it, so the check points at a control on screen.
const REQUIREMENT_LABEL = {
  'ontologies:read': 'Ontology access',
  'passports:write': 'Passports write',
};

/*
 * Everything in the editor state the server would refuse, in the order the
 * editor lays the controls out, each as { domain, code, message, fix? } where
 * `fix` returns the corrected state. Dialogs disable Save while this is
 * non-empty, so the refusal is never a surprise after the round trip, and
 * the editor renders the same list as its Checks with a Fix button beside
 * the ones that have one.
 *
 *   empty_root       Only selected with nothing picked yet — saving would
 *                    grant every ontology, the opposite of what was asked.
 *   stale_narrowing  A dependent's own list shares nothing with the root, so
 *                    editorStateToGrants drops the ability like Grants::cap()
 *                    would; the fix inherits the root again.
 *   dependency       Grants::inconsistencies() on the emitted map — a
 *                    dependent without its root, releasing without writing
 *                    passports; the fix grants the requirement.
 *   ceiling          A level out of the actor's reach, or a reach past their
 *                    own ontologies; the fix, where one exists, narrows to
 *                    what they hold.
 *
 * Only the ceiling part needs a ceiling; the rest holds for an owner too.
 */
export function editorProblems(value, ceiling, availablePermissions) {
  const selected = value?.abilities ?? [];
  const scopes = value?.scopes ?? {};
  const problems = [];
  const has = ability => selected.includes(ability);

  const emptyRoot = has('ontologies:read') && Array.isArray(scopes[ROOT_DOMAIN]) && scopes[ROOT_DOMAIN].length === 0;
  if (emptyRoot) {
    problems.push({
      domain: ROOT_DOMAIN,
      code: 'empty_root',
      message: 'Pick at least one ontology, or choose All ontologies.',
    });
  }

  for (const domain of DEPENDENT_DOMAINS) {
    if (selectedLevel(domain, selected) === 'none') continue;
    const reach = effectiveScope(domain, scopes);
    if (reach !== null && reach.length === 0) {
      problems.push({
        domain,
        code: 'stale_narrowing',
        message: `${titleOf(domain)} is limited to ontologies that are no longer in the ontology selection.`,
        fix: state => {
          const next = { ...(state.scopes || {}) };
          delete next[domain];
          return { abilities: [...(state.abilities || [])], scopes: next };
        },
      });
    }
  }

  // The root and stale checks are each about a domain's ontology list; a
  // ceiling *scope* problem on the same domain would say the same thing
  // twice, so those domains are remembered before the other checks add to
  // the list. A dependency or a ceiling level problem is a different fact.
  const covered = new Set(problems.map(problem => problem.domain));

  // A requirement missing from the emitted map only because its own
  // narrowing went stale is already reported on that domain; blaming its
  // dependant too ("Releases needs Passports write" while Passports shows
  // Read and write) would send the fix to the wrong row. Read and write of
  // one domain fail the same requirement together, and the editor has one
  // row for both, so they get one check.
  const reported = new Set();
  for (const problem of inconsistencies(editorStateToGrants({ abilities: selected, scopes }))) {
    if (covered.has(domainOf(problem.requires))) continue;
    const domain = domainOf(problem.ability);
    const key = `${domain}:${problem.requires}`;
    if (reported.has(key)) continue;
    reported.add(key);
    const needs = REQUIREMENT_LABEL[problem.requires] ?? problem.requires;
    problems.push({
      domain,
      code: 'dependency',
      message: `${titleOf(domain)} needs ${needs} — grant it, or set ${titleOf(domain)} to None.`,
      fix: state => fixFor(problem, state),
    });
  }

  if (!ceiling) return problems;

  for (const meta of abilityDomains(availablePermissions)) {
    const level = selectedLevel(meta.domain, selected);
    if (level === 'none') continue;

    const levels = domainLevels(meta.domain, meta.actions);
    const current = levels.find(l => l.value === level);
    if (!current) continue;

    if (ceilingFor(ceiling, levels).disabledValues.has(level)) {
      problems.push({
        domain: meta.domain,
        code: 'ceiling',
        message: `${meta.title} grants more than you hold — lower it before saving.`,
      });
      continue;
    }

    if (!meta.scopable || covered.has(meta.domain)) continue;

    // A dependent inheriting an "Only selected" root with nothing picked yet
    // reaches nothing definite: its scope is judged once something is, and
    // the empty-root check already holds Save. Its own list is judged as ever.
    if (emptyRoot && DEPENDENT_DOMAINS.includes(meta.domain) && !scopes[meta.domain]?.length) continue;

    const allowed = ceilingScope(ceiling, current.grants);
    const reach = effectiveScope(meta.domain, scopes);
    const problem = scopeProblem(reach, allowed);
    if (!problem) continue;

    // Narrowing to what the actor holds is the one correction that always
    // satisfies the ceiling; when nothing they hold is reachable there is
    // no list to offer.
    const within = reach === null ? allowed : reach.filter(id => allowed.includes(id));
    problems.push({
      domain: meta.domain,
      code: 'ceiling',
      message: `${meta.title}: ${problem}`,
      ...(within.length > 0
        ? { fix: state => ({ abilities: [...(state.abilities || [])], scopes: { ...(state.scopes || {}), [meta.domain]: within } }) }
        : {}),
    });
  }

  return problems;
}

/*
 * A compact reading of a grants map for lists — the roles table and a
 * member's row — where the editor's row-per-domain would be far too much.
 * One entry per domain in catalog order, with the level held and how many
 * ontologies it is limited to (null when team-wide). `everything` is true
 * when every catalog ability is held team-wide, so a row can say "Every
 * permission" instead of listing seventeen domains.
 */
export const SUMMARY_LEVEL = {
  read: 'read',
  write: 'write',
  readwrite: 'read & write',
  use: 'use',
};

export function summariseGrants(grants, catalog = null) {
  const abilities = Object.keys(grants || {});

  const entries = abilityDomains(abilities).map(meta => {
    const slugs = abilities.filter(a => domainOf(a) === meta.domain);
    const scoped = slugs
      .map(a => grants[a])
      .filter(scope => scope && Array.isArray(scope.ontologies));

    return {
      domain: meta.domain,
      title: meta.title,
      level: selectedLevel(meta.domain, abilities),
      ontologies: scoped.length > 0 ? [...new Set(scoped.flatMap(scope => scope.ontologies))] : null,
    };
  });

  const everything =
    Array.isArray(catalog)
    && catalog.length > 0
    && catalog.every(a => holds(grants, a) && (grants[a] === null || grants[a] === undefined));

  return { everything, entries };
}

/*
 * Root grants: the editor state the ontology-root role creator works in.
 *
 *   { abilities: string[], scopes: { [domain]: string[] } }
 *
 * `scopes.ontologies` is the root list — the ontologies `ontologies:read`
 * (and `ontologies:write`) reach. A dependent domain (DEPENDENT_DOMAINS) with
 * no key of its own inherits that list; a key of its own narrows it, and can
 * never widen it: anything it names outside the root is intersected away on
 * the way to the server, and a narrowing that shares nothing with the root
 * drops the ability altogether rather than widening it to every ontology —
 * exactly what Grants::cap() does. An empty list, root or dependent, means
 * "no restriction".
 *
 * Everything here is pure so the creator, the API-key dialog and the summary
 * on a role's row all answer the same questions the same way.
 */

// A scope's ontology list, or null when it restricts nothing. An empty list
// is read as "all" everywhere in this section, so it is normalised here.
const listOf = scope => (scope && Array.isArray(scope.ontologies) && scope.ontologies.length > 0 ? scope.ontologies : null);

const sameList = (a, b) => a.length === b.length && a.every(id => b.includes(id));

// Mirrors Grants::inconsistencies(): a dependent whose requirement is not
// held (`missing`), or one that reaches ontologies its scoped requirement
// does not (`wider`) — `outside` names them, and is empty when the dependent
// is team-wide under a scoped root, the widest possible overreach.
export function inconsistencies(grants) {
  const problems = [];
  for (const [ability, scope] of Object.entries(grants || {})) {
    const requires = REQUIRES[ability];
    if (!requires) continue;
    if (!holds(grants, requires)) {
      problems.push({ ability, requires, kind: 'missing', outside: [] });
      continue;
    }
    if (!ONTOLOGY_SCOPABLE.includes(ability)) continue;
    const root = listOf(grants[requires]);
    if (root === null) continue;
    const mine = listOf(scope);
    if (mine === null) {
      problems.push({ ability, requires, kind: 'wider', outside: [] });
      continue;
    }
    const outside = mine.filter(id => !root.includes(id));
    if (outside.length > 0) problems.push({ ability, requires, kind: 'wider', outside });
  }
  return problems;
}

// The list a domain reaches under the root selection: its own narrowing
// intersected with the root, the root itself when it inherits, and null
// when neither restricts. Null means "no restriction"; an empty result means
// "nothing reachable" — the domain's narrowing shares no ontology with the
// root — and the two must not be confused, because null is every ontology.
// An empty list in `scopes` restricts nothing; ids are deduplicated so the
// client and server representations compare equal.
export function effectiveScope(domain, scopes) {
  const root = scopes?.[ROOT_DOMAIN]?.length ? [...new Set(scopes[ROOT_DOMAIN])] : null;
  if (domain === ROOT_DOMAIN) return root;
  const own = scopes?.[domain]?.length ? [...new Set(scopes[domain])] : null;
  if (own === null) return DEPENDENT_DOMAINS.includes(domain) ? root : null;
  if (root === null || !DEPENDENT_DOMAINS.includes(domain)) return own;
  return own.filter(id => root.includes(id));
}

// Editor state → the Grants map the server speaks ({ ability: null | { ontologies } }).
// Mirrors Grants::cap(): a dependent that reaches nothing under the root is
// left out of the map — emitting null would grant it every ontology instead.
// editorProblems reports that case as stale_narrowing so nothing is dropped
// unseen; a dialog gates Save on it.
export function editorStateToGrants({ abilities = [], scopes = {} }) {
  const grants = {};
  for (const ability of abilities) {
    const list = ONTOLOGY_SCOPABLE.includes(ability) ? effectiveScope(domainOf(ability), scopes) : null;
    if (list === null) {
      grants[ability] = null;
    } else if (list.length > 0) {
      grants[ability] = { ontologies: list };
    }
  }
  return grants;
}

// Grants map → editor state. A dependent scoped to exactly the root's list is
// folded back into inheriting it, so the creator shows one list, not three
// identical ones. A dependent that is team-wide under a scoped root cannot be
// expressed in this shape and comes back as inheriting — which is what the
// server would cap it to anyway. Read and write lists are unioned per
// domain, which is the one thing this shape cannot say apart; the editor
// notes it when a loaded map would come back merged.
export function grantsToEditorState(grants) {
  const abilities = Object.keys(grants || {});
  const byDomain = {};
  for (const ability of abilities) {
    const list = listOf(grants[ability]);
    if (list === null) continue;
    const domain = domainOf(ability);
    byDomain[domain] = [...new Set([...(byDomain[domain] || []), ...list])];
  }

  const root = byDomain[ROOT_DOMAIN] || null;
  const scopes = {};
  for (const [domain, list] of Object.entries(byDomain)) {
    const inherits = root !== null && DEPENDENT_DOMAINS.includes(domain) && sameList(list, root);
    if (!inherits) scopes[domain] = list;
  }

  return { abilities, scopes };
}

// The ability-keyed `scopes` object the key endpoints take, from the editor
// state; null when nothing is scoped. Only granted abilities are sent, which
// is what the server validates.
export function buildScopes(abilities, scopes) {
  const out = {};
  for (const [ability, scope] of Object.entries(editorStateToGrants({ abilities, scopes }))) {
    if (scope) out[ability] = scope;
  }
  return Object.keys(out).length > 0 ? out : null;
}

// The reverse: a key's ability-keyed `scopes` back to per-domain lists.
export function scopesByDomainFromKey(keyScopes) {
  return grantsToEditorState(keyScopes || {}).scopes;
}

// The one-click fix for an inconsistency, as a new state. A missing
// requirement is granted as-is — `ontologies:read` inheriting nothing, so it
// reaches whatever the root list already says; `passports:write` likewise.
// A wider dependent drops its own list and inherits the root's, which is the
// narrowest change that makes it consistent. The root domain has no wider
// list to fall back to, so a problem reported on it leaves the state as is.
export function fixFor(problem, state) {
  const abilities = [...(state.abilities || [])];
  const scopes = { ...(state.scopes || {}) };
  if (problem.kind === 'missing') {
    if (!abilities.includes(problem.requires)) abilities.push(problem.requires);
    return { abilities, scopes };
  }
  const domain = domainOf(problem.ability);
  if (domain === ROOT_DOMAIN) return { abilities, scopes };
  delete scopes[domain];
  return { abilities, scopes };
}

// A grants map as plain sentences, for the creator's live summary and a
// role's detail. `namesById` swaps ontology ids for their names; an id it
// does not know is shown as-is rather than dropped.
export function describeGrants(grants, namesById = {}) {
  const held = Object.keys(grants || {});
  const name = id => namesById[id] || id;
  const join = ids =>
    ids.length <= 2 ? ids.join(' and ') : `${ids.slice(0, -1).join(', ')} and ${ids[ids.length - 1]}`;
  // Mid-sentence a title reads as a common noun ("Reads webhook endpoints."), unless
  // a capital after its first letter marks an acronym or a brand ("API keys",
  // "Synex Lab"), which keeps its case.
  const asNoun = title => (/[A-Z]/.test(title.slice(1)) ? title : title.charAt(0).toLowerCase() + title.slice(1));
  const lines = [];

  const hasRoot = holds(grants, 'ontologies:read');
  const root = hasRoot ? listOf(grants['ontologies:read']) : null;
  if (hasRoot) {
    const where = root ? `${root.length === 1 ? 'ontology' : 'ontologies'} ${join(root.map(name))}` : 'every ontology';
    lines.push(`${holds(grants, 'ontologies:write') ? 'Reads and edits' : 'Reads'} ${where}.`);
  }

  // "their passports" refers back to the root sentence; a map without the
  // root (a legacy grant) has nothing for "their" to point at.
  const whose = hasRoot ? 'their ' : '';
  for (const domain of DEPENDENT_DOMAINS) {
    const level = selectedLevel(domain, held);
    if (level === 'none') continue;
    const verb = { read: 'Reads', write: 'Writes', readwrite: 'Reads and writes' }[level];
    const list = listOf(grants[`${domain}:read`]) || listOf(grants[`${domain}:write`]);
    const narrowed = list && (root === null || !sameList(list, root));
    const what = asNoun(DOMAIN_META[domain].title);
    lines.push(`${verb} ${whose}${what}${narrowed ? ` in ${join(list.map(name))}` : ''}.`);
  }

  if (holds(grants, 'releases:read')) lines.push('Reads release history.');
  if (holds(grants, 'releases:write')) lines.push("Can release passports, and revise or archive released ones (a passport's first release is billed).");
  if (holds(grants, 'consul:use')) lines.push('Uses Consul, the in-app agent.');
  if (holds(grants, 'partner_work:write')) lines.push('Updates passports of linked brands as a service partner.');

  for (const meta of abilityDomains(held)) {
    if (meta.domain === ROOT_DOMAIN || DEPENDENT_DOMAINS.includes(meta.domain) || meta.domain === 'releases' || meta.domain === 'consul' || meta.domain === 'partner_work') continue;
    const level = selectedLevel(meta.domain, held);
    if (level === 'none') continue;
    lines.push(`${{ read: 'Reads', write: 'Writes', readwrite: 'Manages' }[level]} ${asNoun(meta.title)}.`);
  }

  return lines;
}
